/**
 * The Shapiro–Wilk W test of normality, by Royston's algorithm AS R94.
 *
 * Royston, P. (1995). Remark AS R94: A remark on algorithm AS 181: The W-test for
 * normality. *Applied Statistics, 44*(4), 547–551. This is the algorithm behind R's
 * `shapiro.test` and `scipy.stats.shapiro`, ported line for line from R's `swilk.c`:
 *
 * - the coefficients aᵢ come from Blom-type normal scores mᵢ = Φ⁻¹((i − 3/8)/(n + 1/4)),
 *   with the two most extreme corrected by Royston's polynomials in 1/√n;
 * - W is the squared correlation between the ordered data and the coefficients, with
 *   1 − W computed directly so it stays accurate when W is very close to 1;
 * - the p-value is exact for n = 3; for 4 ≤ n ≤ 11 and n ≥ 12 it comes from Royston's
 *   normalising transformations of log(1 − W).
 *
 * Valid for 3 ≤ n ≤ 5000, the range Royston calibrated.
 */

import { mean } from "@/lib/stats/descriptive";
import { normalQuantile, normalUpper } from "@/lib/stats/distributions";

export const MIN_N = 3;
export const MAX_N = 5000;

const SMALL = 1e-19;
const G = [-2.273, 0.459];
const C1 = [0, 0.221_157, -0.147_981, -2.071_19, 4.434_685, -2.706_056];
const C2 = [0, 0.042_981, -0.293_762, -1.752_461, 5.682_633, -3.582_633];
const C3 = [0.544, -0.399_78, 0.025_054, -6.714e-4];
const C4 = [1.3822, -0.778_57, 0.062_767, -0.002_032_2];
const C5 = [-1.5861, -0.310_82, -0.083_751, 0.003_891_5];
const C6 = [-0.4803, -0.082_676, 0.003_030_2];

/** AS 181.2: c[0] + c[1]x + … + c[k−1]x^(k−1). */
function poly(c: readonly number[], x: number): number {
  let result = c.at(-1) as number;
  for (let i = c.length - 2; i >= 0; i--) {
    result = result * x + c[i];
  }
  return result;
}

/** The coefficients a₁ … a_{n/2} (largest first), as AS R94 computes them. */
export function shapiroWilkCoefficients(n: number): number[] {
  const half = Math.floor(n / 2);
  if (n === 3) {
    return [Math.SQRT1_2];
  }
  const m: number[] = [];
  let summ2 = 0;
  for (let i = 1; i <= half; i++) {
    const mi = normalQuantile((i - 0.375) / (n + 0.25));
    m.push(mi);
    summ2 += mi * mi;
  }
  summ2 *= 2;
  const ssumm2 = Math.sqrt(summ2);
  const rsn = 1 / Math.sqrt(n);
  const a1 = poly(C1, rsn) - m[0] / ssumm2;
  const a = new Array<number>(half);
  a[0] = a1;
  let start: number;
  let fac: number;
  if (n > 5) {
    start = 2;
    const a2 = -m[1] / ssumm2 + poly(C2, rsn);
    fac = Math.sqrt(
      (summ2 - 2 * m[0] ** 2 - 2 * m[1] ** 2) / (1 - 2 * a1 ** 2 - 2 * a2 ** 2)
    );
    a[1] = a2;
  } else {
    start = 1;
    fac = Math.sqrt((summ2 - 2 * m[0] ** 2) / (1 - 2 * a1 ** 2));
  }
  for (let i = start; i < half; i++) {
    a[i] = -m[i] / fac;
  }
  return a;
}

function pValue(w: number, w1: number, n: number): number {
  if (n === 3) {
    // Exact: (6/π)(asin √W − asin √¾).
    return Math.max(0, (6 / Math.PI) * (Math.asin(Math.sqrt(w)) - Math.PI / 3));
  }
  let y = Math.log(w1);
  let m: number;
  let s: number;
  if (n <= 11) {
    const gamma = poly(G, n);
    if (y >= gamma) {
      return 1e-99;
    }
    y = -Math.log(gamma - y);
    m = poly(C3, n);
    s = Math.exp(poly(C4, n));
  } else {
    const ln = Math.log(n);
    m = poly(C5, ln);
    s = Math.exp(poly(C6, ln));
  }
  return normalUpper(y, m, s);
}

export interface ShapiroWilkResult {
  n: number;
  w: number;
  p: number;
}

export type ShapiroWilkFailure = "too-few" | "too-many" | "no-range";

export type ShapiroWilkOutcome =
  | { ok: true; result: ShapiroWilkResult }
  | { ok: false; reason: ShapiroWilkFailure };

export function shapiroWilk(values: readonly number[]): ShapiroWilkOutcome {
  const n = values.length;
  if (n < MIN_N) {
    return { ok: false, reason: "too-few" };
  }
  if (n > MAX_N) {
    return { ok: false, reason: "too-many" };
  }
  const x = [...values].sort((p, q) => p - q);
  // Centring on the median first, as SciPy does, changes nothing in exact arithmetic
  // and keeps large offsets from costing digits.
  const centre = x[Math.floor(n / 2)];
  for (let i = 0; i < n; i++) {
    x[i] -= centre;
  }
  const range = x[n - 1] - x[0];
  if (range < SMALL) {
    return { ok: false, reason: "no-range" };
  }
  const half = shapiroWilkCoefficients(n);
  // The full antisymmetric coefficient vector: −a₁ … −a_h, (0), a_h … a₁.
  const coefficients = new Array<number>(n).fill(0);
  for (let i = 0; i < half.length; i++) {
    coefficients[i] = -half[i];
    coefficients[n - 1 - i] = half[i];
  }
  const scaled = x.map((value) => value / range);
  const sa = mean(coefficients);
  const sx = mean(scaled);
  let ssa = 0;
  let ssx = 0;
  let sax = 0;
  for (let i = 0; i < n; i++) {
    const asa = coefficients[i] - sa;
    const xsx = scaled[i] - sx;
    ssa += asa * asa;
    ssx += xsx * xsx;
    sax += asa * xsx;
  }
  const ssassx = Math.sqrt(ssa * ssx);
  const w1 = ((ssassx - sax) * (ssassx + sax)) / (ssa * ssx);
  const w = 1 - w1;
  return { ok: true, result: { n, w, p: pValue(w, w1, n) } };
}

/**
 * Sample skewness G₁ and excess kurtosis G₂, the bias-adjusted estimators SPSS, SAS and
 * Excel (`SKEW`, `KURT`) report — `scipy.stats.skew/kurtosis(bias=False)`.
 */
export function skewnessKurtosis(values: readonly number[]): {
  skewness: number;
  excessKurtosis: number;
} {
  const n = values.length;
  const m = mean(values);
  let m2 = 0;
  let m3 = 0;
  let m4 = 0;
  for (const value of values) {
    const d = value - m;
    m2 += d * d;
    m3 += d * d * d;
    m4 += d * d * d * d;
  }
  m2 /= n;
  m3 /= n;
  m4 /= n;
  const g1 = m3 / m2 ** 1.5;
  const g2 = m4 / (m2 * m2) - 3;
  const skewness = n > 2 ? (Math.sqrt(n * (n - 1)) / (n - 2)) * g1 : Number.NaN;
  const excessKurtosis =
    n > 3 ? ((n - 1) / ((n - 2) * (n - 3))) * ((n + 1) * g2 + 6) : Number.NaN;
  return { skewness, excessKurtosis };
}

/** Normal Q–Q points: the ordered data against Blom scores Φ⁻¹((i − 3/8)/(n + 1/4)). */
export function qqPoints(
  values: readonly number[]
): { theoretical: number; sample: number }[] {
  const n = values.length;
  return [...values]
    .sort((p, q) => p - q)
    .map((sample, i) => ({
      theoretical: normalQuantile((i + 1 - 0.375) / (n + 0.25)),
      sample,
    }));
}
